import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, errorOf, ok } from "./api";
import type { Counts } from "./counts";
import type { DeliveryStatus } from "./labels";

/** A board row (P8-53): the delivery with its place's point, items, time at its step and flags. */
export type BoardRow = components["schemas"]["BoardRow"];
/**
 * What Dispatch's dialogs take: the part of a delivery a board row and a delivery's page share
 * (the board's original row).
 */
export type BoardDelivery = Omit<
  BoardRow,
  | "items"
  | "status_since"
  | "flags"
  | "location"
  | "usual_minutes"
  | "hold_reason"
  | "open_incidents"
  | "delivered_at"
  | "proof"
> & {
  location: { id: string; label: string; floor: number | null } | null;
};

/** The board's segments (design `XzFXn`): what a dispatcher works through, in order. */
export const SEGMENTS = [
  { id: "unassigned", label: "Unassigned", statuses: ["pending", "fillsite_assigned"] },
  { id: "assigned", label: "Assigned", statuses: ["assigned"] },
  {
    id: "in_progress",
    label: "In progress",
    statuses: ["accepted", "at_fillsite", "in_transit", "at_customer", "suspended"],
  },
  { id: "done", label: "Done", statuses: ["delivered"] },
  { id: "closed", label: "Closed", statuses: ["failed", "cancelled", "rerouted"] },
] as const satisfies readonly { id: string; label: string; statuses: readonly DeliveryStatus[] }[];
export type SegmentId = (typeof SEGMENTS)[number]["id"];

export const segmentOf = (status: DeliveryStatus): SegmentId =>
  SEGMENTS.find((s) => (s.statuses as readonly string[]).includes(status))?.id ?? "closed";

/** How many of the day's deliveries are in each segment. */
export function segmentCounts(rows: Pick<BoardDelivery, "status">[]): Record<SegmentId, number> {
  const counts = Object.fromEntries(SEGMENTS.map((s) => [s.id, 0])) as Record<SegmentId, number>;
  for (const row of rows) counts[segmentOf(row.status)] += 1;
  return counts;
}

/**
 * The segment counts the board shows: from its rows, unless the day had more than the board loads
 * (1,000), when the counts endpoint's totals for the day are used instead.
 */
export function boardSegmentCounts(
  rows: Pick<BoardDelivery, "status">[],
  dayTotals?: Pick<Counts, "dispatch">,
): Record<SegmentId, number> {
  const d = dayTotals?.dispatch;
  if (!d) return segmentCounts(rows);
  return { unassigned: d.unassigned, assigned: d.assigned, in_progress: d.in_progress, done: d.done, closed: d.closed };
}

/** The actions a delivery's status (and whether it's offered to partners) allows (modules/dispatch). */
export function actionsFor(status: DeliveryStatus, mode: "manual" | "open" = "manual") {
  const plannable = status === "pending" || status === "fillsite_assigned" || status === "assigned";
  const claimable = status === "pending" || status === "fillsite_assigned";
  return {
    fillsite: plannable,
    assign: plannable,
    unassign: status === "assigned",
    offer: claimable && mode === "manual",
    // take a job back from the partner job board
    takeBack: claimable && mode === "open",
    suspend: status === "at_customer",
    resume: status === "suspended",
    // to their other place at any open stage; another client only once the load is on board (P8-65)
    reroute: !["delivered", "failed", "cancelled", "rerouted"].includes(status),
    // staff mark it delivered for a driver who can't use the app (P8-38)
    complete: status === "at_customer",
    // a problem on any delivery that happened, finished or not: a complaint can come after (P8-31)
    incident: status !== "rerouted" && status !== "cancelled",
    // force reset to pending, at any time; a rerouted one has its replacement (P8-64)
    reset: status !== "pending" && status !== "rerouted",
  };
}

export type Stage = "accepted" | "at_fillsite" | "in_transit" | "at_customer";

/**
 * The stage staff move a delivery on to for its driver (P8-38): a load with a fill site stops
 * there on the way, a parcel doesn't. Null when it isn't on the road with a driver.
 */
export function nextStage(status: DeliveryStatus, hasFillsite: boolean): Stage | null {
  switch (status) {
    case "assigned":
      return "accepted";
    case "accepted":
      return hasFillsite ? "at_fillsite" : "in_transit";
    case "at_fillsite":
      return "in_transit";
    case "in_transit":
      return "at_customer";
    default:
      return null;
  }
}

/** The menu item for each stage: what the driver would have tapped. */
export const STAGE_ACTION: Record<Stage, string> = {
  accepted: "Mark accepted",
  at_fillsite: "Mark at the fill site",
  in_transit: "Mark on the way",
  at_customer: "Mark at the customer",
};

export type CompleteForDriver = components["schemas"]["CompleteForDriverBody"];

/** Where a suspended delivery can go: back where it was, back to planning, or closed. */
export const RESUME_TO = {
  previous: { label: "Where it was before", help: "Carry on as before it was suspended" },
  pending: { label: "Back to planning", help: "It needs a new fill site, truck and driver" },
  failed: { label: "Failed", help: "It won't happen: the customer couldn't take it" },
  cancelled: { label: "Cancelled", help: "Nobody wants it any more" },
} as const;
export type ResumeTo = keyof typeof RESUME_TO;

/** A day is small: the whole board loads at once (up to 1,000), so segments are counted exactly. */
const MAX_PAGES = 10;
export const boardQuery = (date: string, filters: { geofence_id?: string; driver_id?: string }, backlog = false) =>
  queryOptions({
    queryKey: ["dispatch-board", backlog ? "backlog" : date, filters],
    refetchInterval: 30_000,
    queryFn: async () => {
      const rows: BoardRow[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < MAX_PAGES; page++) {
        const { data, error } = await api.GET("/v1/admin/dispatch/board", {
          // the backlog: still to plan from earlier days, whatever day they were for
          params: { query: { ...(backlog ? { backlog: "true" as const } : { date }), ...filters, cursor, limit: 100 } },
        });
        if (error || !data) throw errorOf(error);
        rows.push(...data.data);
        if (!data.next_cursor) return { rows, complete: true };
        cursor = data.next_cursor;
      }
      return { rows, complete: false };
    },
  });

/** What a dispatcher picks from: approved fill sites, drivers on shift (with their crew), approved drivers, zones. */
export const fleetQueries = {
  fillsites: queryOptions({
    queryKey: ["fleet", "fillsites"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/v1/admin/fleet/fillsites");
      if (error || !data) throw errorOf(error);
      return data.data.filter((f) => f.is_active && f.approval_status === "approved");
    },
  }),
  shifts: queryOptions({
    queryKey: ["fleet", "shifts"],
    staleTime: 30_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/v1/admin/fleet/shifts", {
        params: { query: { status: "open", limit: 100 } },
      });
      if (error || !data) throw errorOf(error);
      return data.data;
    },
  }),
  drivers: queryOptions({
    queryKey: ["fleet", "drivers", "approved"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/v1/admin/fleet/drivers", {
        params: { query: { approval_status: "approved", limit: 100 } },
      });
      if (error || !data) throw errorOf(error);
      return data.data;
    },
  }),
  zones: queryOptions({
    queryKey: ["geofences"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await api.GET("/v1/admin/geofences");
      if (error || !data) throw errorOf(error);
      return data.data;
    },
  }),
};

/** The dispatch actions, each one call. Errors come back in the API's shape for messageFor(). */
export const dispatchActions = {
  fillsite: (id: string, fillsite_id: string, reason?: string) =>
    call(
      api.POST("/v1/admin/dispatch/deliveries/{id}/fillsite", {
        params: { path: { id } },
        body: { fillsite_id, reason },
      }),
    ),
  assign: (id: string, body: { driver_id: string; reason?: string }) =>
    call(api.POST("/v1/admin/dispatch/deliveries/{id}/assign", { params: { path: { id } }, body })),
  // another time for a delivery that hasn't left (P8-75)
  reschedule: (id: string, body: { scheduled_at: string; reason: string; notify_customer: boolean }) =>
    call(api.POST("/v1/admin/dispatch/deliveries/{id}/reschedule", { params: { path: { id } }, body })),
  unassign: (id: string, reason: string) =>
    call(api.POST("/v1/admin/dispatch/deliveries/{id}/unassign", { params: { path: { id } }, body: { reason } })),
  reset: (id: string, reason: string) =>
    call(api.POST("/v1/admin/dispatch/deliveries/{id}/reset", { params: { path: { id } }, body: { reason } })),
  suspend: (id: string, reason: string, reason_id?: string) =>
    call(
      api.POST("/v1/admin/dispatch/deliveries/{id}/suspend", { params: { path: { id } }, body: { reason, reason_id } }),
    ),
  resume: (id: string, reason: string, to: ResumeTo = "previous", reason_id?: string) =>
    call(
      api.POST("/v1/admin/dispatch/deliveries/{id}/resume", {
        params: { path: { id } },
        body: { reason, reason_id, ...(to === "previous" ? {} : { to }) },
      }),
    ),
  reroute: (id: string, location_id: string, reason: string, reason_id?: string) =>
    call(
      api.POST("/v1/admin/dispatch/deliveries/{id}/reroute", {
        params: { path: { id } },
        body: { location_id, reason, reason_id },
      }),
    ),
  // another client's waiting delivery takes the load (P8-50)
  rerouteToDelivery: (
    id: string,
    body: {
      target_delivery_id: string;
      first_client: "deliver_later" | "cancel";
      reason: string;
      reason_id?: string;
    },
  ) => call(api.POST("/v1/admin/dispatch/deliveries/{id}/reroute-to-delivery", { params: { path: { id } }, body })),
  progress: (id: string, to: Stage, reason?: string) =>
    call(api.POST("/v1/admin/dispatch/deliveries/{id}/progress", { params: { path: { id } }, body: { to, reason } })),
  complete: (id: string, body: CompleteForDriver) =>
    call(api.POST("/v1/admin/dispatch/deliveries/{id}/complete", { params: { path: { id } }, body })),
  offer: (id: string) =>
    call(api.PUT("/v1/admin/dispatch/deliveries/{id}/job-board", { params: { path: { id } }, body: { mode: "open" } })),
  takeBack: (id: string) =>
    call(
      api.PUT("/v1/admin/dispatch/deliveries/{id}/job-board", { params: { path: { id } }, body: { mode: "manual" } }),
    ),
};

async function call<T>(request: Promise<{ data?: T; error?: unknown }>): Promise<T | undefined> {
  const { data, error } = await request;
  if (error) throw errorOf(error);
  return data;
}

/** What a delivery can be rerouted to: once on the road, the fee and pay and the deliveries that can take its load (P8-50, P8-65). */
export const rerouteOptionsQuery = (id: string) =>
  queryOptions({
    queryKey: ["reroute-options", id],
    queryFn: () => ok(api.GET("/v1/admin/dispatch/deliveries/{id}/reroute-options", { params: { path: { id } } })),
  });

// --- the Dispatch page (P8-53) --------------------------------------------------------------

/** The late limit before the company's settings load (the default, P8-53). */
export const DEFAULT_LATE_MINUTES = 15;

/** The alert limits, for the settings page and the board's hints. */
export const dispatchSettingsQuery = queryOptions({
  queryKey: ["dispatch-settings"],
  staleTime: 60_000,
  queryFn: () => ok(api.GET("/v1/admin/dispatch/settings", {})),
});

const WAITING_FOR_A_TRUCK = new Set(["pending", "fillsite_assigned"]);

/** What needs a look, in the order the page lists it. Each chip filters the queue to its rows. */
export const ATTENTION = [
  { id: "late", label: "running late", tone: "warning", test: (r: BoardRow) => r.flags.late },
  { id: "not_accepted", label: "not accepted", tone: "warning", test: (r: BoardRow) => r.flags.not_accepted },
  { id: "slow", label: "slow at a step", tone: "warning", test: (r: BoardRow) => r.flags.slow },
  { id: "suspended", label: "suspended", tone: "danger", test: (r: BoardRow) => r.status === "suspended" },
  {
    id: "unpaid",
    label: "unpaid, waiting",
    tone: "warning",
    test: (r: BoardRow) =>
      WAITING_FOR_A_TRUCK.has(r.status) &&
      !r.order.postpaid &&
      // a credit order is billed on account: not a payment to chase before dispatch
      !r.order.on_credit &&
      (r.order.payment_status === "unpaid" || r.order.payment_status === "pending"),
  },
] as const;
export type AttentionId = (typeof ATTENTION)[number]["id"];

/** How many of the rows need each kind of look. */
export function attentionCounts(rows: BoardRow[]): Record<AttentionId, number> {
  const counts = Object.fromEntries(ATTENTION.map((a) => [a.id, 0])) as Record<AttentionId, number>;
  for (const r of rows) for (const a of ATTENTION) if (a.test(r)) counts[a.id] += 1;
  return counts;
}

/** Litres a delivery carries (truck loads), to match a truck. */
export const litresOf = (r: Pick<BoardRow, "items">) => r.items.reduce((sum, i) => sum + (i.litres ?? 0), 0);

export type Shift = components["schemas"]["DriverShift"];

export interface Candidate {
  shift: Shift;
  /** no delivery under way right now */
  free: boolean;
  /** its truck carries the load */
  fits: boolean;
  /** straight-line km from where its truck is to the fill site (or the place), when known and fresh */
  km: number | null;
}

/**
 * The drivers on shift for a delivery, best first (P8-53): free ones with a truck big enough,
 * nearest first; then free ones whose truck is too small; then those on a job.
 */
export function bestFit(
  shifts: Shift[],
  litres: number,
  kmOf: (truckId: string) => number | null,
  busy: (shift: Shift) => boolean,
): Candidate[] {
  const rank = (c: Candidate) => (c.free && c.fits ? 0 : c.free ? 1 : 2);
  return shifts
    .filter((s) => s.truck)
    .map((shift) => ({
      shift,
      free: !busy(shift),
      fits: litres === 0 || (shift.truck?.capacity_litres ?? 0) >= litres,
      km: kmOf(shift.truck?.id ?? ""),
    }))
    .sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (a.km ?? Number.POSITIVE_INFINITY) - (b.km ?? Number.POSITIVE_INFINITY) ||
        a.shift.driver.name.localeCompare(b.shift.driver.name),
    );
}

/** "09:30", in the company's zone. */
export const clockOf = (iso: string, timezone: string) =>
  new Intl.DateTimeFormat("en-KE", { timeZone: timezone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(
    new Date(iso),
  );

/** "52 min", "2 h 15 min": how long, from milliseconds. */
export function durationOf(ms: number) {
  // number-ok: minutes, not money
  const minutes = Math.max(0, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const rest = minutes % 60;
  // number-ok: hours, not money
  return rest ? `${Math.floor(minutes / 60)} h ${rest} min` : `${Math.floor(minutes / 60)} h`;
}

/** How long since `iso`, as `durationOf` says it. */
export const sinceOf = (iso: string, now = Date.now()) => durationOf(now - Date.parse(iso));

/** When a delivery is due: its time, else when it was ordered. */
export const dueOf = (r: Pick<BoardRow, "scheduled_at" | "created_at">) => r.scheduled_at ?? r.created_at;

/** "Joseph Mwangi" → "JM". */
export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");

/** Where a delivery's truck could fill up, nearest first, each with its water cost (P8-53). */
export const fillsiteOptionsQuery = (id: string) =>
  queryOptions({
    queryKey: ["fillsite-options", id],
    staleTime: 60_000,
    queryFn: () => ok(api.GET("/v1/admin/dispatch/deliveries/{id}/fillsite-options", { params: { path: { id } } })),
  });

/** What a delivered delivery's card says (`Console / Dispatch / Done`): what needs a look, else what went well. */
export function doneNotes(r: BoardRow, lateAfterMinutes: number): { issues: string[]; good: string[] } {
  const issues: string[] = [];
  const good: string[] = [];
  if (r.proof && !r.proof.photo) issues.push("No proof photo");
  const lateBy = r.delivered_at && r.scheduled_at ? Date.parse(r.delivered_at) - Date.parse(r.scheduled_at) : null;
  if (lateBy !== null && lateBy > lateAfterMinutes * 60_000) issues.push(`${durationOf(lateBy)} late`);
  for (const i of r.open_incidents) issues.push(i.type);
  if (r.proof?.photo) good.push(r.proof.signature ? "Photo · signature" : "Photo");
  if (lateBy !== null && lateBy <= lateAfterMinutes * 60_000) good.push("On time");
  return { issues, good };
}
